import { useEffect, useRef, type ComponentProps } from 'react'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import { venueText, type PublicEvent } from '@utils/embeds/publicEvent'
import { EmbedButton } from './EmbedButton'
import { EventSummary } from './EventSummary'

const t = scopedTranslation('embeds.events.map')

const SWIPE_SETTLE_MS = 150

export function nearestCardIndex(scrollLeft: number, cardOffsets: number[]) {
  if (cardOffsets.length === 0) return null
  return cardOffsets.reduce(
    (nearest, offset, index) =>
      Math.abs(offset - scrollLeft) < Math.abs(cardOffsets[nearest] - scrollLeft) ? index : nearest,
    0
  )
}

type EventStripProps = Pick<ComponentProps<typeof EventSummary>, 'links' | 'rsvpLabel' | 'showCoverage' | 'locale'> & {
  events: PublicEvent[]
  selectedId: number | null
  onSelect: (id: number) => void
}

export function EventStrip({ events, selectedId, onSelect, ...summary }: EventStripProps) {
  const stripRef = useRef<HTMLUListElement>(null)
  const swipedTo = useRef<number | null>(null)
  const settling = useRef<ReturnType<typeof setTimeout>>(undefined)

  const scrollStart = (strip: HTMLElement, card: HTMLElement) =>
    card.offsetLeft - parseFloat(getComputedStyle(strip).paddingLeft)

  const selectSettledCard = () => {
    const strip = stripRef.current
    if (!strip) return
    const cards = Array.from(strip.children) as HTMLElement[]
    const index = nearestCardIndex(
      strip.scrollLeft,
      cards.map((card) => scrollStart(strip, card))
    )
    const settled = index === null ? null : events[index]
    if (!settled || settled.id === selectedId) return
    swipedTo.current = settled.id
    onSelect(settled.id)
  }

  const onScroll = () => {
    clearTimeout(settling.current)
    settling.current = setTimeout(selectSettledCard, SWIPE_SETTLE_MS)
  }

  useEffect(() => () => clearTimeout(settling.current), [])

  useEffect(() => {
    if (selectedId === swipedTo.current) {
      swipedTo.current = null
      return
    }
    const strip = stripRef.current
    const card = strip?.querySelector<HTMLElement>(`[data-event-id="${selectedId}"]`)
    if (strip && card) strip.scrollLeft = scrollStart(strip, card)
  }, [selectedId])

  const position = Math.max(
    0,
    events.findIndex((event) => event.id === selectedId)
  )
  const atStart = position === 0
  const atEnd = position === events.length - 1
  const step = (by: number) => {
    const next = events[position + by]
    if (next) onSelect(next.id)
  }
  const hasControls = events.length > 1

  return (
    <div className="tw:flex tw:flex-col">
      {hasControls && (
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:pt-3">
          <p aria-live="polite" className="tw:text-sm tw:font-medium tw:tabular-nums tw:text-gray-700">
            {t('strip_position', { position: position + 1, count: events.length })}
          </p>
          <div className="tw:flex tw:gap-2">
            <EmbedButton
              color="neutral"
              outline
              aria-label={t('previous_event')}
              aria-disabled={atStart || undefined}
              onClick={() => step(-1)}
            >
              <Icon type="chevron-left" className={cn('tw:h-4 tw:w-4', atStart && 'tw:text-gray-300')} />
            </EmbedButton>
            <EmbedButton
              color="neutral"
              outline
              aria-label={t('next_event')}
              aria-disabled={atEnd || undefined}
              onClick={() => step(1)}
            >
              <Icon type="chevron-right" className={cn('tw:h-4 tw:w-4', atEnd && 'tw:text-gray-300')} />
            </EmbedButton>
          </div>
        </div>
      )}
      <ul
        ref={stripRef}
        aria-label={t('strip_label')}
        onScroll={onScroll}
        className="tw:relative tw:-mx-1 tw:my-0 tw:flex tw:list-none tw:snap-x tw:snap-mandatory tw:scroll-px-1 tw:gap-3 tw:overflow-x-auto tw:px-1 tw:py-3 tw:[scrollbar-width:none] tw:motion-safe:scroll-smooth [&::tw:-webkit-scrollbar]:hidden"
      >
        {events.map((event) => (
          <li
            key={event.id}
            data-event-id={event.id}
            aria-current={event.id === selectedId || undefined}
            className={cn(
              'tw:flex tw:shrink-0 tw:snap-start tw:flex-col tw:rounded-[var(--embed-radius,1rem)] tw:border tw:border-gray-200 tw:bg-white tw:p-4',
              hasControls ? 'tw:w-[85%] tw:max-w-[20rem]' : 'tw:w-full',
              event.id === selectedId && 'tw:ring-2 tw:ring-[color:var(--embed-brand)]'
            )}
          >
            <EventSummary event={event} {...summary} titleClassName="tw:line-clamp-2">
              <p className="tw:m-0 tw:truncate tw:text-xs tw:text-gray-500">{venueText(event.venue)}</p>
            </EventSummary>
          </li>
        ))}
      </ul>
    </div>
  )
}
